import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Latching visibility: `inView` turns true the first time the element comes
 * within `rootMargin` of the viewport and stays true (a lazily mounted query
 * should not unsubscribe when the user scrolls past it).
 *
 * Without IntersectionObserver (old browsers) the element counts as visible
 * once mounted, so nothing is ever left unloaded. During server rendering /
 * before mount it is false.
 */
export function useInView<T extends Element>(rootMargin = '200px'): [(node: T | null) => void, boolean] {
  const [inView, setInView] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);

  const disconnect = () => {
    observer.current?.disconnect();
    observer.current = null;
  };

  const ref = useCallback(
    (node: T | null) => {
      disconnect();
      if (!node || inView) return;
      if (typeof IntersectionObserver === 'undefined') {
        setInView(true);
        return;
      }
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setInView(true);
            disconnect();
          }
        },
        { rootMargin },
      );
      observer.current.observe(node);
    },
    [inView, rootMargin],
  );

  useEffect(() => disconnect, []);

  return [ref, inView];
}
